'use client'

import { useState, useRef } from 'react'
import type { ReactNode } from 'react'
import {
  SHOW_PRICING, MARKETING_CSS, Icon, ICONS, CheckRow,
  NavBar, MarketingFooter, useMarketingReveal,
} from './marketing-ui'

// ─── Types ─────────────────────────────────────────────────────────────────
interface NewsletterForm {
  name: string
  email: string
  brandName: string
  website: string
}

interface BetaForm {
  name: string
  email: string
  brandName: string
  websiteInstagram: string
  teamSize: string
  currentTools: string
  biggestChallenge: string
  whyBeta: string
}

// ─── Browser frame helper ────────────────────────────────────────────────────
function BrowserFrame({ url, children }: { url: string; children: ReactNode }) {
  return (
    <div className="mk-frame">
      <div className="mk-frame-bar">
        <div style={{ display: 'flex', gap: 5 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#E8C2B2' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#EBD9A4' }} />
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#B3D4BB' }} />
        </div>
        <div style={{
          flex: 1, maxWidth: 280, marginLeft: 10,
          backgroundColor: '#E5E3DA', borderRadius: 5,
          height: 22, display: 'flex', alignItems: 'center', paddingLeft: 10,
        }}>
          <span style={{ fontSize: 11, color: '#6A6A62' }}>{url}</span>
        </div>
      </div>
      {children}
    </div>
  )
}

function Screenshot({ src, alt, ratio, eager }: { src: string; alt: string; ratio: string; eager?: boolean }) {
  return (
    <div style={{ aspectRatio: ratio, overflow: 'hidden', backgroundColor: '#F4F2EC' }}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
        decoding="async"
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left', display: 'block' }}
      />
    </div>
  )
}

// ─── Newsletter Form ─────────────────────────────────────────────────────────
function NewsletterSignupForm() {
  const [form, setForm] = useState<NewsletterForm>({ name: '', email: '', brandName: '', website: '' })
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')
    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw new Error('Submission failed')
      setStatus('success')
    } catch {
      setStatus('error')
      setErrorMsg('Something went wrong. Please try again.')
    }
  }

  if (status === 'success') {
    return (
      <div style={{
        padding: '28px 24px', backgroundColor: 'var(--mk-green-soft)',
        borderRadius: 12, border: '1px solid var(--mk-green-border)', textAlign: 'center',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--mk-green)' }}>
          <Icon d={ICONS.check} size={28} />
        </div>
        <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--mk-green)', marginBottom: 4 }}>Check your inbox.</p>
        <p style={{ fontSize: 14, color: 'var(--mk-green-text)', margin: 0 }}>Click the link we just sent to confirm your subscription.</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="mk-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label className="mk-label" htmlFor="nl-name">Name *</label>
          <input
            id="nl-name"
            required
            placeholder="Your name"
            autoComplete="name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="mk-input"
          />
        </div>
        <div>
          <label className="mk-label" htmlFor="nl-email">Email *</label>
          <input
            id="nl-email"
            required
            type="email"
            placeholder="you@yourbrand.com"
            autoComplete="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            className="mk-input"
          />
        </div>
        <div>
          <label className="mk-label" htmlFor="nl-brand">Brand Name</label>
          <input
            id="nl-brand"
            placeholder="Your brand"
            value={form.brandName}
            onChange={e => setForm(f => ({ ...f, brandName: e.target.value }))}
            className="mk-input"
          />
        </div>
        <div>
          <label className="mk-label" htmlFor="nl-site">Website</label>
          <input
            id="nl-site"
            placeholder="yoursite.com"
            value={form.website}
            onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
            className="mk-input"
          />
        </div>
      </div>
      {errorMsg && (
        <p style={{ fontSize: 13, color: 'var(--mk-error)', margin: 0 }} role="alert">{errorMsg}</p>
      )}
      <button type="submit" disabled={status === 'loading'} className="mk-btn" style={{ marginTop: 4 }}>
        {status === 'loading' ? 'Submitting…' : 'Keep me posted'}
      </button>
    </form>
  )
}

// ─── Beta Application Form ───────────────────────────────────────────────────
function BetaApplicationForm() {
  const [form, setForm] = useState<BetaForm>({
    name: '', email: '', brandName: '', websiteInstagram: '',
    teamSize: '', currentTools: '', biggestChallenge: '', whyBeta: '',
  })
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')
    try {
      const res = await fetch('/api/beta-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw new Error('Submission failed')
      setStatus('success')
    } catch {
      setStatus('error')
      setErrorMsg('Something went wrong. Please try again.')
    }
  }

  if (status === 'success') {
    return (
      <div style={{
        padding: '40px 32px', backgroundColor: 'var(--mk-green-soft)',
        borderRadius: 12, border: '1px solid var(--mk-green-border)', textAlign: 'center',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--mk-green)' }}>
          <Icon d={ICONS.check} size={32} />
        </div>
        <p style={{ fontSize: 18, fontWeight: 700, color: 'var(--mk-green)', marginBottom: 8 }}>Application received.</p>
        <p style={{ fontSize: 15, color: 'var(--mk-green-text)', maxWidth: 400, margin: '0 auto' }}>
          We review every application personally. If your brand is a good fit, we&apos;ll be in touch.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="mk-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label className="mk-label" htmlFor="ba-name">Name *</label>
          <input id="ba-name" required placeholder="Your name" autoComplete="name" value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mk-input" />
        </div>
        <div>
          <label className="mk-label" htmlFor="ba-email">Email *</label>
          <input id="ba-email" required type="email" placeholder="you@yourbrand.com" autoComplete="email" value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mk-input" />
        </div>
        <div>
          <label className="mk-label" htmlFor="ba-brand">Brand Name *</label>
          <input id="ba-brand" required placeholder="Your brand" value={form.brandName}
            onChange={e => setForm(f => ({ ...f, brandName: e.target.value }))} className="mk-input" />
        </div>
        <div>
          <label className="mk-label" htmlFor="ba-site">Website / Instagram</label>
          <input id="ba-site" placeholder="yoursite.com or @handle" value={form.websiteInstagram}
            onChange={e => setForm(f => ({ ...f, websiteInstagram: e.target.value }))} className="mk-input" />
        </div>
      </div>
      <div>
        <label className="mk-label" htmlFor="ba-team">Team Size</label>
        <select id="ba-team" value={form.teamSize}
          onChange={e => setForm(f => ({ ...f, teamSize: e.target.value }))}
          className="mk-input" style={{ appearance: 'none' as const }}>
          <option value="">Select team size</option>
          <option value="1">Just me</option>
          <option value="2-3">2–3 people</option>
          <option value="4-5">4–5 people</option>
          <option value="6-10">6–10 people</option>
          <option value="10+">10+ people</option>
        </select>
      </div>
      <div>
        <label className="mk-label" htmlFor="ba-tools">What tools are you currently using?</label>
        <input id="ba-tools" placeholder="e.g. Notion, Dropbox, spreadsheets, Airtable…" value={form.currentTools}
          onChange={e => setForm(f => ({ ...f, currentTools: e.target.value }))} className="mk-input" />
      </div>
      <div>
        <label className="mk-label" htmlFor="ba-challenge">What&apos;s your biggest operational challenge right now?</label>
        <textarea
          id="ba-challenge"
          rows={3}
          placeholder="Tell us what's most painful about running your brand today"
          value={form.biggestChallenge}
          onChange={e => setForm(f => ({ ...f, biggestChallenge: e.target.value }))}
          className="mk-input"
          style={{ resize: 'vertical' as const, minHeight: 80 }}
        />
      </div>
      <div>
        <label className="mk-label" htmlFor="ba-why">Why do you want beta access?</label>
        <textarea
          id="ba-why"
          rows={3}
          placeholder="What would you use Obi-Hub for, and what would make it valuable for your brand?"
          value={form.whyBeta}
          onChange={e => setForm(f => ({ ...f, whyBeta: e.target.value }))}
          className="mk-input"
          style={{ resize: 'vertical' as const, minHeight: 80 }}
        />
      </div>
      {errorMsg && (
        <p style={{ fontSize: 13, color: 'var(--mk-error)', margin: 0 }} role="alert">{errorMsg}</p>
      )}
      <button type="submit" disabled={status === 'loading'} className="mk-btn">
        {status === 'loading' ? 'Submitting…' : 'Apply for beta access'}
      </button>
    </form>
  )
}

// ─── FAQ data & component ────────────────────────────────────────────────────
const faqs = [
  {
    q: 'Who is Obi-Hub for?',
    a: 'Obi-Hub is built for small clothing brands — typically teams of 1 to 10 people who are already operating and need a better way to manage product development, files, tasks, content, and team coordination in one place.',
  },
  {
    q: 'Is it only for clothing brands?',
    a: 'Yes, for now. The platform is purpose-built for apparel — the workflows, terminology, and structure are all designed around how clothing brands actually operate. It is not a generic project management tool.',
  },
  {
    q: 'Does Obi-Hub connect to Shopify?',
    a: 'Yes. Connect your store and your product catalog syncs in automatically. Link Shopify products to the styles they came from, and orders sync daily so sales show up in your reports. The Shopify connection is included with the platform — there is no separate charge for it.',
  },
  {
    q: 'Does Obi-Hub handle social content too?',
    a: 'Yes. The Content Studio runs a full editorial pipeline — briefing, writing, creative, filming, editing, review, and scheduling — with a live Instagram preview that uses your brand’s accent color and handle, so what you approve is what your followers see.',
  },
  {
    q: 'Can my factory or ambassadors access Obi-Hub?',
    a: 'Yes — without giving them full logins. Production orders generate tokenized share links so factories can see files, post comments, and update status without seeing the rest of your business. Ambassadors get their own branded portal to receive products and upload content.',
  },
  {
    q: 'What does private beta mean?',
    a: 'Private beta means the product is real and being used in production today by a working clothing brand. We are onboarding a limited number of additional brands carefully — making sure every brand that joins gets real value and has a chance to help shape the product.',
  },
  {
    q: 'What do beta testers get?',
    a: 'Selected beta brands may receive up to 3 months of free access. More importantly, you get direct input into how the platform develops — your feedback shapes what gets built next.',
  },
  {
    q: 'When are you launching publicly?',
    a: 'We are not announcing a specific date yet. We are focused on getting the product right with a small group of brands before opening it up more broadly. Applying keeps you at the front of the line.',
  },
  // Pricing FAQ hidden during beta — see SHOW_PRICING
  ...(SHOW_PRICING ? [{
    q: 'Will pricing change before launch?',
    a: 'Possibly. The pricing shown here reflects our current thinking, but it may evolve before the full public launch. We will be transparent with early users about any changes.',
  }] : []),
  {
    q: 'Can I join the list if I am not ready for beta?',
    a: 'Absolutely. The newsletter signup is for anyone who wants to stay informed. You do not need to be ready to use the product today — just sign up and we will keep you posted.',
  },
]

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ borderBottom: '1px solid var(--mk-line)' }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '20px 0',
          background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--mk-ink)', paddingRight: 24 }}>{q}</span>
        <span style={{
          flexShrink: 0, width: 24, height: 24, borderRadius: '50%',
          backgroundColor: open ? 'var(--mk-green)' : '#EFEDE5',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 0.15s',
        }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            {open
              ? <line x1="3" y1="6" x2="9" y2="6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
              : <>
                  <line x1="6" y1="3" x2="6" y2="9" stroke="#4A4A45" strokeWidth="1.5" strokeLinecap="round"/>
                  <line x1="3" y1="6" x2="9" y2="6" stroke="#4A4A45" strokeWidth="1.5" strokeLinecap="round"/>
                </>
            }
          </svg>
        </span>
      </button>
      <div style={{
        display: 'grid',
        gridTemplateRows: open ? '1fr' : '0fr',
        transition: 'grid-template-rows 0.3s var(--mk-ease-out)',
      }}>
        <div style={{ overflow: 'hidden' }} aria-hidden={!open}>
          <p style={{ fontSize: 15, color: 'var(--mk-body)', lineHeight: 1.65, margin: 0, paddingBottom: 20 }}>{a}</p>
        </div>
      </div>
    </div>
  )
}

// ─── Feature grid card ───────────────────────────────────────────────────────
function FeatureCard({
  eyebrow, title, body, screenshot, screenshotAlt, ratio,
}: {
  eyebrow: string
  title: string
  body: string
  screenshot: string
  screenshotAlt: string
  ratio: string
}) {
  return (
    <div className="mk-feature-card">
      <div style={{ height: 180, overflow: 'hidden', borderBottom: '1px solid var(--mk-line-soft)', backgroundColor: '#F4F2EC' }}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={screenshot}
          alt={screenshotAlt}
          loading="lazy"
          decoding="async"
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'top left', display: 'block',
            aspectRatio: ratio,
          }}
        />
      </div>
      <div style={{ padding: '22px 22px 24px' }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--mk-green)',
          letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8,
        }}>
          {eyebrow}
        </div>
        <h3 style={{ fontSize: 17.5, fontWeight: 700, color: 'var(--mk-ink)', margin: '0 0 8px', lineHeight: 1.3 }}>
          {title}
        </h3>
        <p style={{ fontSize: 14, color: 'var(--mk-body)', lineHeight: 1.6, margin: 0 }}>
          {body}
        </p>
      </div>
    </div>
  )
}

// ─── Main Marketing Page ───────────────────────────────────────────────────
export default function MarketingHomePage({
  pricingSection,
  displayFontClass = '',
}: {
  pricingSection?: ReactNode
  displayFontClass?: string
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  useMarketingReveal(rootRef)

  return (
    <div ref={rootRef} className={`mk ${displayFontClass}`}>
      <style>{MARKETING_CSS}</style>
      <NavBar />
      <main id="main-content">

      {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
      <section style={{ paddingTop: 140, paddingBottom: 88, position: 'relative', overflow: 'hidden' }}>
        {/* soft radial wash behind the hero */}
        <div aria-hidden="true" style={{
          position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)',
          width: 1100, height: 700, borderRadius: '50%',
          background: 'radial-gradient(closest-side, rgba(46,85,69,0.08), rgba(46,85,69,0))',
          pointerEvents: 'none',
        }} />
        <div className="mk-container" style={{ position: 'relative' }}>
          <div className="mk-hero-seq" style={{ textAlign: 'center', maxWidth: 800, margin: '0 auto 56px' }}>
            <div style={{ marginBottom: 26 }}>
              <span className="mk-badge">Private beta · Now accepting brand applications</span>
            </div>

            <p className="mk-eyebrow mk-eyebrow--center">One Brand Interface</p>
            <h1 className="mk-display" style={{ fontSize: 'clamp(40px, 5.6vw, 68px)', marginBottom: 24 }}>
              The operating system for
              <em style={{ display: 'block' }}>small clothing brands</em>
            </h1>

            <p className="mk-lead" style={{ fontSize: 'clamp(16px, 2vw, 19px)', maxWidth: 660, margin: '0 auto 36px' }}>
              The operational backbone big apparel companies build in-house — design, production,
              content, ambassadors, and your Shopify store in one connected workspace. Sized for
              teams of one to ten.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginBottom: 20 }}>
              <a href="#beta" className="mk-btn">
                Apply for beta access
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="#product" className="mk-btn mk-btn--ghost">
                See what&apos;s inside
              </a>
            </div>

            <p style={{ fontSize: 13, color: 'var(--mk-faint)', margin: 0 }}>
              Running in production today at a working clothing brand.
            </p>
          </div>

          {/* Hero product preview — real screenshot */}
          <div className="mk-hero-frame">
            <BrowserFrame url="app.obi-hub.app/home">
              <Screenshot src="/screenshots/home.webp" alt="OBI-Hub home dashboard with launch snapshot, tasks, and production status" ratio="2880 / 1880" eager />
            </BrowserFrame>
          </div>

          {/* Module ticker — duplicated set drives the seamless marquee loop */}
          <div className="mk-ticker" aria-label="Platform modules">
            <div className="mk-ticker-track">
              {[false, true].map(dup =>
                ['Product Dev', 'Tech Packs', 'Samples', 'Orders', 'Vendor Portal', 'Content Studio', 'Ambassadors', 'Library', 'Shopify Sync', 'Reports'].map(m => (
                  <span key={`${m}${dup ? '-dup' : ''}`} aria-hidden={dup || undefined}>{m}</span>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. PROBLEM ──────────────────────────────────────────────────── */}
      <section className="mk-section mk-section--white mk-stitch-top" data-reveal>
        <div className="mk-container">
          <div style={{ maxWidth: 620, marginBottom: 28 }}>
            <h2 className="mk-display mk-h2">
              Running a brand shouldn&apos;t mean running <em>ten different tools</em>
            </h2>
            <p className="mk-lead" style={{ fontSize: 16 }}>
              Big brands solve this with in-house systems and an ops team. Small brands get
              spreadsheets — and spend more time managing tools than making product.
            </p>
          </div>

          <div className="mk-ledger" data-reveal data-stagger>
            {[
              { icon: ICONS.sheet, title: 'Spreadsheets everywhere', body: 'Product tracking, costing, orders — all in separate sheets that never quite stay in sync.' },
              { icon: ICONS.folder, title: 'Files scattered across folders', body: 'Tech packs in Dropbox, brand assets in Google Drive, sample photos in WhatsApp. Nothing is where you expect it.' },
              { icon: ICONS.checkSquare, title: 'Tasks in generic tools', body: 'Notion, Asana, Trello — none of them understand what a tech pack is or how a sampling round works.' },
              { icon: ICONS.scatter, title: 'No single source of truth', body: 'Your team is working from different versions of the same information. Decisions get made on outdated data.' },
              { icon: ICONS.coins, title: 'Rising software costs', body: "You're paying for five tools that each do one thing. The total adds up fast for a small team." },
              { icon: ICONS.unlink, title: 'Disconnected workflows', body: "When a product changes, nothing else updates automatically. You're manually keeping everything in sync." },
            ].map((card, i) => (
              <div key={i} className="mk-ledger-row">
                <div className="mk-icon-tile">
                  <Icon d={card.icon} />
                </div>
                <div>
                  <h3 style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--mk-ink)', margin: '0 0 6px' }}>{card.title}</h3>
                  <p style={{ fontSize: 14, color: 'var(--mk-body)', lineHeight: 1.6, margin: 0 }}>{card.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. SOLUTION ─────────────────────────────────────────────────── */}
      <section className="mk-section mk-stitch-top" data-reveal>
        <div className="mk-container">
          <div className="mk-split">
            <div>
              <h2 className="mk-display mk-h2">
                Everything your brand needs, <em>actually connected</em>
              </h2>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 18 }}>
                Obi-Hub is not another project management tool with a new coat of paint. It is the
                operational backbone a 300-person apparel company has — product development, production tracking,
                content ops, partner portals — rebuilt at the scale and price of a 3-person brand.
              </p>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 30 }}>
                When a product moves through development, your tasks, files, and samples move with it.
                When an order is placed, it links back to the product. When a sale happens on your
                store, it shows up in your reports. Everything stays in sync because it was built to
                work together.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  'Product development — concept through production',
                  'Tech packs, colorway sheets, samples, and designs',
                  'Tasks that link to products, orders, and content',
                  'Production orders with portal access for every vendor',
                  'Content Studio with live Instagram preview',
                  'Ambassadors with their own self-serve portal',
                  'Brand guide, photography, and shared team knowledge',
                  'Shopify sync and sales reporting built in',
                ].map((item, i) => (
                  <CheckRow key={i}>{item}</CheckRow>
                ))}
              </div>
            </div>
            {/* Tasks screenshot */}
            <div>
              <BrowserFrame url="app.obi-hub.app/tasks">
                <Screenshot src="/screenshots/tasks.webp" alt="OBI-Hub tasks view with statuses, priorities, and linked projects" ratio="2880 / 2400" />
              </BrowserFrame>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. FEATURE GRID — 7 modules ────────────────────────────────── */}
      <section id="product" className="mk-section mk-section--white mk-stitch-top" data-reveal>
        <div className="mk-container">
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
            <p className="mk-eyebrow mk-eyebrow--center">The platform</p>
            <h2 className="mk-display mk-h2">
              Seven surfaces. <em>One Brand Interface.</em>
            </h2>
            <p className="mk-lead" style={{ fontSize: 16 }}>
              It&apos;s in the name — OBI stands for <strong style={{ fontWeight: 600, color: 'var(--mk-green)' }}>O</strong>ne{' '}
              <strong style={{ fontWeight: 600, color: 'var(--mk-green)' }}>B</strong>rand{' '}
              <strong style={{ fontWeight: 600, color: 'var(--mk-green)' }}>I</strong>nterface. Every part of
              Obi-Hub is built around how clothing brands actually operate, and everything connects
              back to the product.
            </p>
          </div>

          <div className="mk-grid-3" data-reveal data-stagger>
            <FeatureCard
              eyebrow="Product Development"
              title="Every style, sketch to shelf"
              body="A visual board of every product in your line, organized by season, stage, and status. Each style links to its tech pack, colorways, samples, and tasks."
              screenshot="/screenshots/product-dev.webp"
              screenshotAlt="Product Development board"
              ratio="2880 / 2136"
            />
            <FeatureCard
              eyebrow="Tech Packs & Colorways"
              title="Versioned files, tied to products"
              body="Tech packs, colorway sheets, and design files — versioned, approved, and linked to the styles that use them. No more digging through Dropbox."
              screenshot="/screenshots/tech-packs.webp"
              screenshotAlt="Tech Packs view"
              ratio="2880 / 2232"
            />
            <FeatureCard
              eyebrow="Tasks & Inbox"
              title="Tasks that understand apparel"
              body="Every task attaches to something real — a product, tech pack, order, or content card. Your inbox surfaces exactly what needs you and why."
              screenshot="/screenshots/tasks.webp"
              screenshotAlt="Inbox view"
              ratio="2880 / 2400"
            />
            <FeatureCard
              eyebrow="Orders & Vendor Portal"
              title="Production across every factory"
              body="Log production orders, track delivery, and share a tokenized link with each vendor so they can see files and update status — without seeing the rest of your business."
              screenshot="/screenshots/orders.webp"
              screenshotAlt="Production orders"
              ratio="2880 / 1800"
            />
            <FeatureCard
              eyebrow="Content Studio"
              title="From idea to Instagram"
              body="A complete content pipeline — brief, write, film, edit, approve, schedule. Live Instagram preview rendered with your brand&rsquo;s color and handle so what you approve is what posts."
              screenshot="/screenshots/content-studio.webp"
              screenshotAlt="Content Studio editor with Instagram preview"
              ratio="2880 / 1800"
            />
            <FeatureCard
              eyebrow="Ambassadors"
              title="Their own login, not a spreadsheet"
              body="Ambassadors get a branded self-serve portal — see assignments, upload content, message you. You see everything in one feed."
              screenshot="/screenshots/ambassadors.webp"
              screenshotAlt="Ambassador detail view"
              ratio="2880 / 1800"
            />
            <FeatureCard
              eyebrow="Library & Brand Guide"
              title="One source of truth"
              body="Brand guide, photography, knowledge base, and resources — organized for your team, ambassadors, and vendors. Stop pasting Dropbox links."
              screenshot="/screenshots/library.webp"
              screenshotAlt="Library view"
              ratio="2880 / 1800"
            />
          </div>
        </div>
      </section>

      {/* ── 5. DEEP DIVE — Content Studio ──────────────────────────────── */}
      <section className="mk-section mk-stitch-top" data-reveal>
        <div className="mk-container">
          <div className="mk-split">
            <div>
              <p className="mk-eyebrow">Content Studio</p>
              <h2 className="mk-display mk-h3">
                Write, schedule, and see <em>exactly how it&apos;ll look</em>
              </h2>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 18 }}>
                A complete editorial pipeline — briefing, writing, creative direction, filming,
                editing, review, and scheduling — built into the same workspace as your products.
              </p>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 26 }}>
                Every content card has a live Instagram preview rendered with your brand&apos;s accent
                color and handle. What you approve is what your followers see. Tie posts to the styles
                they promote, hand off filming to the right person, and never lose a caption to a
                Slack thread again.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  'Full pipeline from idea to scheduled',
                  'Live Instagram mockup with your brand styling',
                  'Captions, hashtags, pillars, campaigns, and assignees',
                  'Approval status separate from production stage',
                ].map((item, i) => (
                  <CheckRow key={i}>{item}</CheckRow>
                ))}
              </div>
            </div>
            <div>
              <BrowserFrame url="app.obi-hub.app/content">
                <Screenshot src="/screenshots/content-studio.webp" alt="Content Studio with live Instagram preview" ratio="2880 / 1800" />
              </BrowserFrame>
            </div>
          </div>
        </div>
      </section>

      {/* ── 6. DEEP DIVE — Ambassadors ─────────────────────────────────── */}
      <section className="mk-section mk-section--white mk-stitch-top" data-reveal>
        <div className="mk-container">
          <div className="mk-split">
            <div className="mk-split-media--lead">
              <BrowserFrame url="app.obi-hub.app/creators/ambassadors">
                <Screenshot src="/screenshots/ambassadors.webp" alt="Ambassador profile with requests, messages, and social profiles" ratio="2880 / 1800" />
              </BrowserFrame>
            </div>
            <div>
              <p className="mk-eyebrow">Ambassador Portal</p>
              <h2 className="mk-display mk-h3">
                Give your ambassadors <em>their own login</em>
              </h2>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 18 }}>
                Send products, collect content, and track every collaboration in one place. Your
                ambassadors get a clean, branded portal where they can see assignments, upload
                deliverables, and message you directly.
              </p>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 26 }}>
                No more shared spreadsheets, no more email chains, no more wondering whether the next
                drop&apos;s content is ready. The activity feed shows you what every ambassador is
                doing, and uploads land in your library tagged to the right product.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  'Token-authenticated portal — no full app login required',
                  'Product assignments link to your real catalog',
                  'Uploads tag to the right products automatically',
                  'Direct messaging and activity feed per ambassador',
                ].map((item, i) => (
                  <CheckRow key={i}>{item}</CheckRow>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. DEEP DIVE — Vendor Portal ───────────────────────────────── */}
      <section className="mk-section mk-stitch-top" data-reveal>
        <div className="mk-container">
          <div className="mk-split">
            <div>
              <p className="mk-eyebrow">Vendor Portal</p>
              <h2 className="mk-display mk-h3">
                Share an order link. <em>They see what they need, nothing else.</em>
              </h2>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 18 }}>
                Production runs live in Obi-Hub, but your factory doesn&apos;t need a full login to
                participate. Generate a tokenized share link for any order — your vendor can view
                files, post comments, and update milestones without seeing the rest of your business.
              </p>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 26 }}>
                No more emailing tech packs back and forth. No more chasing for status updates. The
                vendor portal is scoped to the order — they see exactly what they need to deliver, and
                you see every comment and file in the same place as the rest of the production thread.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  'Scoped tokenized links per order — no broader access',
                  'Shared files, comments, and status updates',
                  'Vendor activity appears in your main order view',
                  'Revoke or rotate links anytime',
                ].map((item, i) => (
                  <CheckRow key={i}>{item}</CheckRow>
                ))}
              </div>
            </div>
            <div>
              <BrowserFrame url="portal.obi-hub.app/order/ORD-2026-002">
                <Screenshot src="/screenshots/vendor-portal.webp" alt="Shared production order with line items, files, and vendor comments" ratio="2880 / 1800" />
              </BrowserFrame>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. COMMERCE BAND — Shopify + Reports ───────────────────────── */}
      <section className="mk-section mk-section--dark mk-dark">
        <div className="mk-container">
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 56px' }}>
            <p className="mk-eyebrow mk-eyebrow--center" style={{ color: 'var(--mk-green-pale)' }}>Connected to commerce</p>
            <h2 className="mk-display mk-h2">
              It talks to <em>your store</em>, too
            </h2>
            <p className="mk-lead" style={{ fontSize: 16 }}>
              The same workspace that runs your production also knows what&apos;s selling. Connect
              Shopify once and your catalog, orders, and sales flow into the picture.
            </p>
          </div>

          <div className="mk-grid-2" style={{ gap: 24, maxWidth: 920, margin: '0 auto' }}>
            {[
              {
                icon: ICONS.store,
                title: 'Shopify Sync',
                body: 'Connect your store and your product catalog pulls in automatically. Link Shopify listings to the styles they came from, and orders sync daily.',
                bullets: [
                  'Store products link to your real catalog',
                  'Orders and sales sync automatically',
                  'The Shopify connection is included — no extra charge',
                ],
              },
              {
                icon: ICONS.chart,
                title: 'Reports & Insights',
                body: 'Production reports, sales metrics, and goal tracking — computed from the same data the rest of your workspace already runs on.',
                bullets: [
                  'Sales rolled up by style and season',
                  'Production and sample-round reporting',
                  'KPIs and goals on your home dashboard',
                ],
              },
            ].map((col, i) => (
              <div key={i} style={{
                padding: '32px 30px', borderRadius: 16,
                border: '1px dashed rgba(159, 195, 178, 0.4)',
                backgroundColor: 'rgba(255,255,255,0.03)',
              }}>
                <div className="mk-icon-tile" style={{ backgroundColor: 'rgba(159,195,178,0.14)', color: 'var(--mk-green-pale)', marginBottom: 16 }}>
                  <Icon d={col.icon} />
                </div>
                <h3 style={{ fontSize: 19, fontWeight: 700, color: '#F5F4EC', margin: '0 0 10px' }}>{col.title}</h3>
                <p style={{ fontSize: 15, lineHeight: 1.65, margin: '0 0 18px' }}>{col.body}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {col.bullets.map((b, j) => (
                    <CheckRow key={j} dark>{b}</CheckRow>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. APPAREL-NATIVE PROOF ────────────────────────────────────── */}
      <section className="mk-section mk-section--white">
        <div className="mk-container" style={{ maxWidth: 1100 }}>
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 48px' }}>
            <h2 className="mk-display" style={{ fontSize: 'clamp(26px, 3vw, 36px)', marginBottom: 14 }}>
              Workflows Notion and Asana <em>can&apos;t fake</em>
            </h2>
            <p style={{ fontSize: 15, color: 'var(--mk-body)', lineHeight: 1.65, margin: 0 }}>
              This is the discipline big apparel brands run — sample rounds, approval gates, version
              control — without the enterprise software or the ops headcount. The structure
              isn&apos;t configurable, it&apos;s baked in.
            </p>
          </div>

          <div className="mk-ruled" data-reveal data-stagger>
            {[
              {
                title: '3-round sample review',
                body: 'Approve, reject, or request changes on every sample round. Escalate after round 3 — without losing the thread.',
              },
              {
                title: '14-stage product journey',
                body: 'From idea to live on site. Always know exactly where every style is in development and production.',
              },
              {
                title: 'Versioned approval gates',
                body: 'Upload a new tech pack version and the status flips back to review automatically. No manual chasing.',
              },
              {
                title: 'Tasks that attach to anything',
                body: 'A single task can attach to a product, tech pack, colorway, order, or content card — whatever it actually relates to.',
              },
            ].map((item, i) => (
              <div key={i}>
                <h3 className="mk-display" style={{ fontSize: 19, fontWeight: 400, color: 'var(--mk-green)', margin: '0 0 10px' }}>
                  {item.title}
                </h3>
                <p style={{ fontSize: 14, color: 'var(--mk-body)', lineHeight: 1.6, margin: 0 }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9b. MID-PAGE CTA ────────────────────────────────────────────── */}
      <section className="mk-section mk-stitch-top" data-reveal style={{ padding: '72px 24px' }}>
        <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="mk-display" style={{ fontSize: 'clamp(26px, 3vw, 36px)', marginBottom: 14 }}>
            Stop managing tools. <em>Start running your brand.</em>
          </h2>
          <p className="mk-lead" style={{ fontSize: 16, marginBottom: 28 }}>
            See every feature in detail, or apply now and we&apos;ll walk you through it with your
            own products.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            <a href="#beta" className="mk-btn">Apply for beta access</a>
            <a href="/features" className="mk-btn mk-btn--ghost">Browse all features</a>
          </div>
        </div>
      </section>

      {/* ── 10. FOUNDER STORY ───────────────────────────────────────────── */}
      <section className="mk-section mk-stitch-top" data-reveal>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 18,
            border: '1px solid var(--mk-line)', padding: 'clamp(28px, 5vw, 52px)',
            boxShadow: '0 4px 28px rgba(20, 20, 15, 0.05)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 30 }}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/logo-text-green.png" alt="OBI-Hub" style={{ height: 28, width: 'auto' }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--mk-ink)' }}>Built by a real clothing brand</div>
                <div style={{ fontSize: 13, color: 'var(--mk-faint)' }}>The origin of Obi-Hub</div>
              </div>
            </div>

            <blockquote style={{ margin: '0 0 28px', padding: 0 }}>
              <p className="mk-display" style={{
                fontSize: 'clamp(21px, 2.7vw, 27px)', fontStyle: 'italic',
                lineHeight: 1.5, color: '#1F2E27',
              }}>
                &ldquo;We built Obi-Hub because we were living the problem ourselves. Spreadsheets for
                products, Dropbox for files, Notion for tasks, WhatsApp for everything else. Nothing
                talked to anything. We kept losing track of where things were and who was doing
                what.&rdquo;
              </p>
            </blockquote>

            <p style={{ fontSize: 16, color: 'var(--mk-body)', lineHeight: 1.7, marginBottom: 18, marginTop: 0 }}>
              Obi-Hub started as an internal tool — built to solve the operational chaos of running a
              small clothing brand. We needed something that understood how apparel actually works:
              product stages, tech packs, sampling rounds, colorways, manufacturing orders, content
              drops, ambassador campaigns.
            </p>
            <p style={{ fontSize: 16, color: 'var(--mk-body)', lineHeight: 1.7, marginBottom: 28, marginTop: 0 }}>
              After building it for ourselves and using it every day, we realized other small brands
              were dealing with the exact same fragmented stack. So we&apos;re opening it up. Obi-Hub
              is now being built for every small brand that deserves better tools than a folder full
              of spreadsheets.
            </p>

            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 16,
              padding: '20px', backgroundColor: 'var(--mk-bg)',
              borderRadius: 10, border: '1px dashed var(--mk-line)',
            }}>
              {['Built by a real brand', 'Used in production daily', 'Designed for small teams', 'Apparel-native workflows'].map((tag, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--mk-green)' }} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--mk-green-text)' }}>{tag}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 11. PRICING ───────────────────────────────────────────────── */}
      {/* Hidden during beta — see SHOW_PRICING */}
      {SHOW_PRICING && pricingSection}

      {/* ── 12. BETA SECTION — PRIMARY CONVERSION ───────────────────────── */}
      <section id="beta" className="mk-section mk-section--white mk-stitch-top" data-reveal>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div className="mk-split mk-split--start">
            <div>
              <span className="mk-badge" style={{ marginBottom: 20 }}>Private beta</span>
              <h2 className="mk-display mk-h2" style={{ marginTop: 14 }}>
                Apply for <em>beta access</em>
              </h2>
              <p className="mk-lead" style={{ fontSize: 16, marginBottom: 28 }}>
                We&apos;re onboarding a limited number of brands into the private beta. This
                isn&apos;t a waitlist — it&apos;s a selective process to find brands that will get
                real value from the platform and help shape its direction.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { icon: ICONS.users, title: 'Best fit', body: 'Already launched clothing brands with small operating teams (1–10 people).' },
                  { icon: ICONS.star, title: 'What you get', body: 'Selected brands may receive up to 3 months of free access.' },
                  { icon: ICONS.message, title: 'What we ask', body: 'Honest feedback. You help shape what gets built next.' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 14 }}>
                    <div className="mk-icon-tile" style={{ width: 36, height: 36, borderRadius: 9 }}>
                      <Icon d={item.icon} size={18} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--mk-ink)', marginBottom: 4 }}>{item.title}</div>
                      <div style={{ fontSize: 14, color: 'var(--mk-body)', lineHeight: 1.6 }}>{item.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{
              padding: 'clamp(20px, 3vw, 30px)', borderRadius: 16,
              backgroundColor: 'var(--mk-bg)', border: '1px solid #EFEDE5',
            }}>
              <BetaApplicationForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── 13. FAQ ─────────────────────────────────────────────────────── */}
      <section id="faq" className="mk-section mk-stitch-top" data-reveal>
        {/* FAQPage structured data for search engines */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            }),
          }}
        />
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 className="mk-display" style={{ fontSize: 'clamp(26px, 3vw, 36px)' }}>
              Common questions
            </h2>
          </div>
          <div style={{ borderTop: '1px solid var(--mk-line)' }}>
            {faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── 14. NEWSLETTER — secondary CTA ──────────────────────────────── */}
      <section id="newsletter" style={{ padding: '80px 24px', backgroundColor: 'var(--mk-green)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 className="mk-display" style={{ fontSize: 'clamp(24px, 2.8vw, 32px)', color: '#fff', marginBottom: 12 }}>
              Not ready to apply? <em style={{ color: '#B9D6C8' }}>Stay close to the launch.</em>
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, margin: 0 }}>
              We&apos;ll send launch updates, new feature news, and the occasional honest update from
              the team. No spam.
            </p>
          </div>
          <div style={{
            backgroundColor: '#fff', borderRadius: 14,
            padding: '28px', boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          }}>
            <NewsletterSignupForm />
            <p style={{ fontSize: 12, color: 'var(--mk-faint)', textAlign: 'center', margin: '16px 0 0' }}>
              No spam. Unsubscribe any time.
            </p>
          </div>
        </div>
      </section>

      </main>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <MarketingFooter />
    </div>
  )
}
